import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular/messages';
import { TranslateService } from '@ngx-translate/core';
import { of, catchError, map, mergeMap, tap } from 'rxjs';
import { finalizeLoader } from '@app/shared/components';
import { ToastErrorService } from '@app/shared/services';
import { courseActions } from '../actions/course.actions';
import { HttpService } from '../services/http.service';

@Injectable()
export class CourseEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);
    private readonly toastErrorService = inject(ToastErrorService);

    getCourse$ = createEffect(() => {
        return this.actions.pipe(
            ofType(courseActions.getCourse),
            mergeMap(({ id }) => {
                return this.httpService.getCourse(id).pipe(
                    finalizeLoader('course-get'),
                    map((course) => courseActions.getCourseSuccess({ payload: course })),
                    catchError((response: HttpErrorResponse) => of(courseActions.getCourseError({ code: response.status }))),
                );
            }),
        );
    });

    createCourse$ = createEffect(() => {
        return this.actions.pipe(
            ofType(courseActions.createCourse),
            mergeMap(({ value }) => {
                return this.httpService.createCourse(value).pipe(
                    finalizeLoader('course-create'),
                    map((course) => courseActions.createCourseSuccess({ payload: course })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.courses.created'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.courses.createError');
                        return of(courseActions.createCourseError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    updateCourse$ = createEffect(() => {
        return this.actions.pipe(
            ofType(courseActions.updateCourse),
            mergeMap(({ id, value }) => {
                return this.httpService.updateCourse(id, value).pipe(
                    finalizeLoader('course-update'),
                    map((course) => courseActions.updateCourseSuccess({ payload: course })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.courses.updated'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.courses.updateError');
                        return of(courseActions.updateCourseError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    deleteCourse$ = createEffect(() => {
        return this.actions.pipe(
            ofType(courseActions.deleteCourse),
            mergeMap(({ id }) => {
                return this.httpService.deleteCourse(id).pipe(
                    finalizeLoader('course-delete'),
                    map(() => courseActions.deleteCourseSuccess({ id })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.courses.deleted'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.courses.deleteError');
                        return of(courseActions.deleteCourseError({ code: response.status }));
                    }),
                );
            }),
        );
    });
}
